// src/pages/InvoiceDetailPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useData } from "../DataContext";
import { apiRequest } from "../api/http";
import {
  apiInvoiceToUi,
  deleteInvoiceById,
  getInvoiceById,
  UiInvoice,
  updateInvoiceById,
  uiInvoiceToApi,
} from "../api/invoices";

import InvoicePrintPreview from "../components/InvoicePrintPreview";

type LineItem = {
  description: string;
  amount: number;
  taxable: boolean; // editor only (we'll hide in print later)
};

type Adjustment = { label?: string; amount?: number };

type ApiOk<T> = { ok: true; data: T };
type ApiErr = { ok: false; error: string };

// ---- Payments Received (API + UI) ----
type ApiPaymentReceived = {
  id: string;
  tenant_id: string;
  project_id: string;
  amount: number;
  method: "Cash" | "Bank" | string;
  date: string; // YYYY-MM-DD
  notes: string;
  created_at?: string;
  updated_at?: string;
};

type UiPaymentReceived = {
  id: string;
  projectId: string;
  amount: number;
  method: "Cash" | "Bank";
  date: string;
  notes: string;
};

function paymentToUi(row: any): UiPaymentReceived {
  const raw =
    (row?.method ?? row?.paymentMethod ?? row?.payment_method ?? "")
      .toString()
      .trim()
      .toLowerCase();

  const m: "Cash" | "Bank" = raw.includes("bank") ? "Bank" : "Cash";

  return {
    id: String(row.id),
    projectId: String(row.project_id ?? row.projectId ?? ""),
    amount: Number(row.amount) || 0,
    method: m,
    date: (row.date || "").toString(),
    notes: (row.notes || "").toString(),
  };
}


type ApiScopeItem = {
  id: string;
  tenant_id: string;
  project_id: string;
  job_area: string;
  description: string;
  estimate: number;
  created_at?: string;
  updated_at?: string;
};

const money = (n: number) =>
  `$${(Number(n) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

function safeJson<T>(s: string, fallback: T): T {
  try {
    const v = JSON.parse(s);
    return (v ?? fallback) as T;
  } catch {
    return fallback;
  }
}

function compactJson(obj: any) {
  try {
    return JSON.stringify(obj ?? [], null, 0);
  } catch {
    return "[]";
  }
}

function parseIdsJson(s: string) {
  const arr = safeJson<any[]>(s || "[]", []);
  return arr.map((x) => String(x)).filter(Boolean);
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function computeTotals(inv: UiInvoice, items: LineItem[]) {
  const subtotal = items.reduce((sum, it) => sum + (Number(it.amount) || 0), 0);

  const adjustments = safeJson<Adjustment[]>(inv.adjustmentsJson || "[]", []);
  const adjTotal = adjustments.reduce((sum, a) => sum + (Number(a.amount) || 0), 0);

  const taxableSubtotal = items.reduce(
    (sum, it) => (it.taxable ? sum + (Number(it.amount) || 0) : sum),
    0
  );

  const taxRate = Number(inv.taxRate) || 0; // stored as decimal, e.g. 0.06625
  const tax = inv.taxEnabled ? taxableSubtotal * taxRate : 0;

  const total = subtotal + adjTotal + tax;
  return { subtotal, adjTotal, taxableSubtotal, tax, total };
}

function normalizeItemsFromJson(itemsJson: string) {
  const raw = safeJson<any[]>(itemsJson || "[]", []);
  const items: LineItem[] = raw.map((r) => ({
    description: (r?.description ?? r?.label ?? r?.name ?? "").toString(),
    amount: Number(r?.amount) || 0,
    taxable: Boolean(r?.taxable),
  }));
  return items.length
    ? items
    : [
        {
          description: "",
          amount: 0,
          taxable: false,
        },
      ];
}

export default function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const data = useData() as any;
  const { t } = useTranslation();

  const tenantId: string = (data?.activeTenantId || data?.tenantId || "demo").toString();

  // project/client data (tenant-scoped)
  const projects = useMemo(() => (data?.projects || []) as any[], [data?.projects]);
  const clients  = useMemo(() => (data?.clients  || []) as any[], [data?.clients]);


  const projectOptions = useMemo(() => {
    const list = [...projects];
    return list.sort((a: any, b: any) => {
      const la = `${a?.name || ""}${a?.address ? ` — ${a.address}` : ""}`.trim();
      const lb = `${b?.name || ""}${b?.address ? ` — ${b.address}` : ""}`.trim();
      return la.localeCompare(lb);
    });
  }, [projects]);

  function getProjectById(pid: string | null) {
    if (!pid) return null;
    return projects.find((p: any) => p.id === pid) || null;
  }

  function getClientForProject(p: any) {
    if (!p) return null;
    const cid = p.clientId || p.client_id || "";
    if (!cid) return null;
    return clients.find((c: any) => c.id === cid) || null;
  }

  function getProjectLabel(p: any) {
    if (!p) return "";

    const name = (p?.name || "").toString().trim();
    const addr = (p?.address || "").toString().trim();
    const base = `${name}${addr ? ` — ${addr}` : ""}`.trim();

    const cid = (p?.clientId || p?.client_id || "").toString().trim();
    const c = cid ? clients.find((x: any) => x.id === cid) : null;
    const clientName = (c?.name || "").toString().trim();

    // "Client · Project — Address"
    return clientName ? `${clientName} · ${base}` : base;
  }

  // ---------------- invoice state ----------------
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [base, setBase] = useState<UiInvoice | null>(null); // last saved snapshot
  const [draft, setDraft] = useState<UiInvoice | null>(null); // editor draft
  const [items, setItems] = useState<LineItem[]>([{ description: "", amount: 0, taxable: false }]);
  const [taxRateTouched, setTaxRateTouched] = useState(false);

  // ---------------- payments-applied state ----------------
  const [payRows, setPayRows] = useState<UiPaymentReceived[]>([]);
  const [payLoading, setPayLoading] = useState(false);
  const [payErr, setPayErr] = useState<string | null>(null);

  // ---------------- company info (for print preview header) ----------------
  const [companyInfo, setCompanyInfo] = useState<any | null>(null);
  const [companyInfoErr, setCompanyInfoErr] = useState<string | null>(null);

  async function loadCompanyInfo() {
    try {
      setCompanyInfoErr(null);

      // If your DataContext already has it, prefer that (no API call needed)
      // Use DataContext only if it actually has a populated name.
     // (DataContext may initialize companyInfo as {} which is truthy and would wrongly skip API.)
     const ctx = data?.companyInfo;
     const ctxName =
    (ctx?.legalName ?? ctx?.legal_name ?? "").toString().trim();

        if (ctx && ctxName) {
        setCompanyInfo(ctx);
        return;
        }

      // Otherwise load from API
      const res = await apiRequest<ApiOk<any>>("GET", "/api/company", { tenantId });
      setCompanyInfo(res.data ?? null);


      const raw = (res as any).data;

        // normalize common API shapes:
        // 1) data is an array -> take first row
        // 2) data.rows is an array -> take first row
        // 3) otherwise assume object
        const normalized =
        Array.isArray(raw) ? raw[0] :
        Array.isArray(raw?.rows) ? raw.rows[0] :
        raw;

        setCompanyInfo(normalized ?? null);


    } catch (e: any) {
      setCompanyInfo(null);
      setCompanyInfoErr(e?.message || "Failed to load company info");
    }
  }

  useEffect(() => {
    loadCompanyInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  // ---------- load invoice ----------
  useEffect(() => {
    async function load() {
      if (!id) return;
      setLoading(true);
      setErr(null);
      try {
        const res = await getInvoiceById(tenantId, id);
        if (!res.ok) {
          setErr(res.error || "Failed to load invoice");
          setBase(null);
          setDraft(null);
          return;
        }

        const ui = apiInvoiceToUi(res.data as any);
        const loadedItems = normalizeItemsFromJson(ui.itemsJson);

        setBase(ui);
        setDraft(ui);
        setItems(loadedItems);
      } catch (e: any) {
        setErr(e?.message || String(e));
        setBase(null);
        setDraft(null);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [tenantId, id]);

  const totals = useMemo(() => {
    if (!draft) return { subtotal: 0, adjTotal: 0, taxableSubtotal: 0, tax: 0, total: 0 };
    return computeTotals(draft, items);
  }, [draft, items]);

  const dirty = useMemo(() => {
    if (!base || !draft) return false;
    const snapBase = {
      ...base,
      itemsJson: compactJson(normalizeItemsFromJson(base.itemsJson)),
    };
    const snapDraft = {
      ...draft,
      itemsJson: compactJson(items),
    };
    return JSON.stringify(snapBase) !== JSON.stringify(snapDraft);
  }, [base, draft, items]);

  // ---------- editor helpers ----------
  function setDraftField<K extends keyof UiInvoice>(key: K, value: UiInvoice[K]) {
    setDraft((d) => (d ? { ...d, [key]: value } : d));
  }

  function setItem(idx: number, patch: Partial<LineItem>) {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }

  function addItem() {
    setItems((prev) => [...prev, { description: "", amount: 0, taxable: false }]);
  }

  function removeItem(idx: number) {
    setItems((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      return next.length ? next : [{ description: "", amount: 0, taxable: false }];
    });
  }

  // ---------- project defaults (bill-to + scope -> invoice line items) ----------
  async function applyProjectDefaults(pid: string | null) {
    if (!pid) return;
    const p = getProjectById(pid);
    if (!p) return;

    const c = getClientForProject(p);

    // 1) Apply bill-to + label
    setDraft((d) => {
      if (!d) return d;
      return {
        ...d,
        projectId: p.id,
        projectLabel: getProjectLabel(p) || d.projectLabel,
        billToName: (c?.name || d.billToName || "").toString(),
        billToAddress: (c?.address || d.billToAddress || "").toString(),
        scopeText: (d.scopeText || "").trim()
          ? d.scopeText
          : p?.address
          ? `Work at ${p.address}`
          : d.scopeText,
      };
    });

    // 2) Load project scope rows -> invoice line items
    try {
      const res = await apiRequest<ApiOk<ApiScopeItem[]> | ApiErr>(
        "GET",
        `/api/scope?project_id=${encodeURIComponent(p.id)}`,
        { tenantId }
      );

      if (!(res as any).ok) return;

      const rows = (((res as any).data || []) as ApiScopeItem[]) || [];
      if (!rows.length) return;

      const ok = window.confirm(t("invoiceDetail.loadScopeConfirm", { count: rows.length }));
      if (!ok) return;

      setItems(
        rows
          .slice()
          .reverse()
          .map((r) => ({
            description: `${(r.job_area || "").trim()}: ${(r.description || "").trim()}`.trim(),
            amount: Number(r.estimate) || 0,
            taxable: false,
          }))
      );
    } catch {
      // silent fail: do not block invoice editing
    }
  }

  // ---------- payments-applied helpers ----------
  const appliedIds = useMemo(() => {
    const ids = draft ? parseIdsJson((draft as any).appliedPaymentIdsJson || "[]") : [];
    return new Set(ids);
  }, [draft]);

  const appliedTotal = useMemo(() => {
    if (!draft) return 0;
    let sum = 0;
    for (const p of payRows) {
      if (appliedIds.has(p.id)) sum += Number(p.amount) || 0;
    }
    return sum;
  }, [payRows, appliedIds, draft]);

  async function loadPaymentsForProject(pid: string | null) {
    if (!pid) {
      setPayRows([]);
      setPayErr(null);
      return;
    }

    setPayLoading(true);
    setPayErr(null);
    try {
      const res = await apiRequest<ApiOk<ApiPaymentReceived[]> | ApiErr>(
        "GET",
        `/api/payments_received?project_id=${encodeURIComponent(pid)}`,
        { tenantId }
      );

      if (!(res as any).ok) {
        setPayErr((res as any).error || "Failed to load payments");
        setPayRows([]);
        return;
      }

      const rows = (((res as any).data || []) as ApiPaymentReceived[]) || [];
      setPayRows(rows.map(paymentToUi));
    } catch (e: any) {
      setPayErr(e?.message || "Failed to load payments");
      setPayRows([]);
    } finally {
      setPayLoading(false);
    }
  }

  useEffect(() => {
    if (!draft) return;
    loadPaymentsForProject(draft.projectId || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, draft?.projectId]);

  function toggleAppliedPayment(paymentId: string) {
    setDraft((d) => {
      if (!d) return d;
      const ids = new Set(parseIdsJson((d as any).appliedPaymentIdsJson || "[]"));
      if (ids.has(paymentId)) ids.delete(paymentId);
      else ids.add(paymentId);
      return { ...(d as any), appliedPaymentIdsJson: compactJson(Array.from(ids)) };
    });
  }

  // ---------- actions ----------

  function onPrint() {
  // optional: scroll to preview so user sees what will print
  const el = document.getElementById("invoice-print-preview");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });

  // give the scroll a beat to settle
  setTimeout(() => window.print(), 150);
    }

  async function onSave() {
    if (!draft || !id) return;

    setSaving(true);
    setErr(null);
    try {
      const body = uiInvoiceToApi({
        ...draft,
        itemsJson: compactJson(items),
      });

      const res = await updateInvoiceById(tenantId, id, body);

      if (!res.ok) {
        setErr(res.error || "Save failed");
        return;
      }

      const ui = apiInvoiceToUi(res.data as any);
      setBase(ui);
      setDraft(ui);
      setItems(normalizeItemsFromJson(ui.itemsJson));
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally {
      setSaving(false);
    }
  }

  async function onToggleFinal() {
    if (!draft || !id) return;

    const nextStatus = draft.status === "final" ? "draft" : "final";
    const ok = window.confirm(
      nextStatus === "final"
        ? t("invoiceDetail.confirmFinalize")
        : t("invoiceDetail.confirmRevertDraft")
    );
    if (!ok) return;

    setSaving(true);
    setErr(null);
    try {
      const res = await updateInvoiceById(tenantId, id, { status: nextStatus });
      if (!res.ok) {
        setErr(res.error || "Update failed");
        return;
      }

      const ui = apiInvoiceToUi(res.data as any);
      setBase(ui);
      setDraft(ui);
      setItems(normalizeItemsFromJson(ui.itemsJson));
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally {
      setSaving(false);
    }
  }

  async function onDelete() {
    if (!draft || !id) return;

    const ok = window.confirm(t("invoiceDetail.confirmDelete"));
    if (!ok) return;

    setSaving(true);
    setErr(null);
    try {
      const res = await deleteInvoiceById(tenantId, id);
      if (!res.ok) {
        setErr(res.error || "Delete failed");
        return;
      }
      navigate("/invoices");
    } catch (e: any) {
      setErr(e?.message || String(e));
    } finally {
      setSaving(false);
    }
  }

  // ---------- render ----------
  const headerNumber = draft?.invoiceNumber || id || "Invoice";
  const canDelete = draft?.status === "draft";

  return (
    <div className="container">
      <div className="noPrint">
      <style>{`
        /* ---------- Grid safety + full-width controls ---------- */
        .invCoreGrid .input,
        .invProjectGrid .input,
        .invBillToGrid .input,
        .invPayGrid .input{
            width: 100%;
            max-width: 100%;
            box-sizing: border-box;
        }

        .invCoreGrid .field,
        .invProjectGrid .field,
        .invBillToGrid .field,
        .invPayGrid .field{
            min-width: 0;           /* prevents overflow in grid cells */
        }

        /* If any label/note is long, allow wrapping instead of pushing layout */
        .invCoreGrid .label,
        .invProjectGrid .label,
        .invBillToGrid .label{
            white-space: normal;
        }

        /* ---------- Core grid ---------- */
        .invCoreGrid{
            display:grid;
            grid-template-columns: minmax(0,1.2fr) minmax(0,0.8fr) minmax(0,0.8fr) minmax(0,0.8fr);
            gap:14px;
            align-items:end;
        }

        /* ---------- Project row (fixes your alignment issue) ---------- */
        .invProjectGrid{
            display:grid;
            grid-template-columns: minmax(0,1.2fr) minmax(0,1fr);
            gap:14px;

            /* IMPORTANT: keeps Project Label aligned to the top of Project select,
            even when left side has notes/buttons making it taller */
            align-items:start;
        }

        /* Keep the two inputs aligned even if one field has a note below */
        .invProjectGrid .field{
            align-self:start;
        }

        /* ---------- Bill To grid ---------- */
        .invBillToGrid{
            display:grid;
            grid-template-columns: minmax(0,1fr) minmax(0,1fr);
            gap:14px;
            align-items:end;
        }

        /* ---------- Payments add row layout (more responsive) ---------- */
        .invPayGrid{
            display:grid;
            gap:12px;
            align-items:end;

            /* auto-wrap instead of overlapping on narrower widths */
            grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
        }

        /* ---------- Breakpoints ---------- */
        @media (max-width: 980px){
            .invCoreGrid{ grid-template-columns: repeat(2, minmax(0, 1fr)); }
            .invProjectGrid{ grid-template-columns: 1fr; } /* stack project + label */
            .invBillToGrid{ grid-template-columns: 1fr; }  /* stack bill-to fields */
        }

        @media (max-width: 640px){
            .invCoreGrid{ grid-template-columns: 1fr; }
        }

        .payRowActions{
            display:flex;
            gap:8px;
            justify-content:flex-end;
            flex-wrap:wrap;
        }

        .numRight{ text-align:right; white-space:nowrap; }
        .mutedError{ color: var(--bad); font-weight: 800; }

        /* ---------- PRINT: only print the preview area ---------- */
        @media print {
        /* remove app background + spacing */
        html, body {
            background: #fff !important;
            margin: 0 !important;
            padding: 0 !important;
        }

        /* hide everything by default */
        body * {
            visibility: hidden !important;
        }

        /* show ONLY the printArea subtree */
        .printArea, .printArea * {
            visibility: visible !important;
        }

        /* place the printArea at the very top-left so it doesn't start mid-page */
        .printArea {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            background: #fff !important;
        }

        /* kill card styling that can create shaded/tinted print previews */
        .card, .cardTight {
            box-shadow: none !important;
            background: transparent !important;
        }

        /* optional: control page margins */
        @page {
            margin: 12mm;
        }
        }

    `}</style>


      {/* header row */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "end",
          flexWrap: "wrap",
        }}
      >
        <div>
          <button
            onClick={() => navigate("/invoices")}
            className="btn btnSm btnSecondary"
            type="button"
            style={{ marginBottom: 6 }}
          >
            {t("invoiceDetail.backBtn")}
          </button>

          <h1 style={{ marginBottom: 6 }}>{t("invoiceDetail.title")}</h1>
          <div className="pageNote">{headerNumber}</div>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <button
            className="btn btnSm"
            type="button"
            onClick={onSave}
            disabled={loading || saving || !dirty || !draft}
            title={!dirty ? t("invoiceDetail.noChanges") : t("invoiceDetail.saveBtn")}
          >
            {saving ? t("invoiceDetail.saving") : t("invoiceDetail.saveBtn")}
          </button>

          <button
            className="btn btnSm btnSecondary"
            type="button"
            onClick={onToggleFinal}
            disabled={loading || saving || !draft}
          >
            {draft?.status === "final" ? t("invoiceDetail.revertDraftBtn") : t("invoiceDetail.finalizeBtn")}
          </button>

          <button
            className="btn btnSm btnDanger"
            type="button"
            onClick={onDelete}
            disabled={loading || saving || !draft || !canDelete}
            title={!canDelete ? t("invoiceDetail.onlyDraftDelete") : t("invoiceDetail.deleteBtn")}
          >
            {t("invoiceDetail.deleteBtn")}
          </button>
          <button
            className="btn btnSm btnSecondary"
            type="button"
            onClick={onPrint}
            disabled={loading || !draft}
            title={t("invoiceDetail.printBtn")}
            >
            {t("invoiceDetail.printBtn")}
          </button>

        </div>
      </div>

      {err ? (
        <div className="card cardTight" style={{ marginTop: 14, marginBottom: 14 }}>
          <div className="formError" role="alert">
            {err}
          </div>
        </div>
      ) : null}

      {loading || !draft ? (
        <div className="card cardTight" style={{ marginTop: 14 }}>
          <div className="pageNote">{loading ? t("invoiceDetail.saving") : t("invoiceDetail.noInvoiceLoaded")}</div>
        </div>
      ) : (
        <>
          {/* Core */}
          <div className="card cardTight" style={{ marginTop: 14 }}>
            <div className="cardHeader">
              <h3 className="cardTitle">{t("invoiceDetail.coreTitle")}</h3>
              <div className="cardSub">{dirty ? t("invoiceDetail.unsavedChanges") : t("invoiceDetail.upToDate")}</div>
            </div>

            <div className="invCoreGrid">
              <div className="field">
                <label className="label">{t("invoiceDetail.invoiceNumLabel")}</label>
                <input
                  className="input"
                  value={draft.invoiceNumber}
                  onChange={(e) => setDraftField("invoiceNumber", e.target.value)}
                  placeholder={t("invoiceDetail.invoiceNumberPlaceholder")}
                />
              </div>

              <div className="field">
                <label className="label">{t("invoiceDetail.typeLabel")}</label>
                <select
                  className="input"
                  value={draft.invoiceType}
                  onChange={(e) =>
                    setDraftField(
                      "invoiceType",
                      (e.target.value === "change_order" ? "change_order" : "detailed") as any
                    )
                  }
                >
                  <option value="detailed">{t("invoiceDetail.typeDetailed")}</option>
                  <option value="change_order">{t("invoiceDetail.typeChangeOrder")}</option>
                </select>
              </div>

              <div className="field">
                <label className="label">{t("invoiceDetail.invoiceDateLabel")}</label>
                <input
                  className="input"
                  type="date"
                  value={draft.invoiceDate}
                  onChange={(e) => setDraftField("invoiceDate", e.target.value)}
                />
              </div>

              <div className="field">
                <label className="label">{t("invoiceDetail.dueDateLabel")}</label>
                <input
                  className="input"
                  type="date"
                  value={draft.dueDate || ""}
                  onChange={(e) => setDraftField("dueDate", e.target.value ? e.target.value : (null as any))}
                />
              </div>

              <div style={{ gridColumn: "1 / -1" }}>
                {/* Project row aligned with Project Label */}
                <div className="invProjectGrid">
                  <div className="field">
                    <label className="label">{t("invoiceDetail.projectOptional")}</label>
                    <select
                      className="input"
                      value={draft.projectId || ""}
                      onChange={(e) => {
                        const pid = e.target.value ? e.target.value : null;
                        setDraft((d) => {
                          if (!d) return d;
                          const p = pid ? getProjectById(pid) : null;
                          return {
                            ...d,
                            projectId: pid,
                            projectLabel: p ? getProjectLabel(p) : d.projectLabel,
                          };
                        });
                      }}
                    >
                      <option value="">{t("invoiceDetail.oneOff")}</option>
                      {projectOptions.map((p: any) => (
                        <option key={p.id} value={p.id}>
                          {getProjectLabel(p) || p.id}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="field">
                    <label className="label">{t("invoiceDetail.projectLabelLabel")}</label>
                    <input
                      className="input"
                      value={draft.projectLabel || ""}
                      onChange={(e) => setDraftField("projectLabel", e.target.value)}
                      placeholder={t("invoiceDetail.projectLabelPlaceholder")}
                    />
                    <div className="pageNote" style={{ marginTop: 6 }}>
                      {t("invoiceDetail.projectLabelTip")}
                    </div>
                  </div>
                </div>

                <div className="pageNote" style={{ marginTop: 6 }}>
                  {t("invoiceDetail.oneOffNote")}
                </div>

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
                  <button
                    className="btn btnSm"
                    type="button"
                    onClick={() => applyProjectDefaults(draft.projectId || null)}
                    disabled={!draft.projectId}
                    title={!draft.projectId ? t("invoiceDetail.selectProjectFirst") : t("invoiceDetail.fillBillToNote")}
                  >
                    {t("invoiceDetail.applyDefaultsBtn")}
                  </button>

                  <button
                    className="btn btnSm btnSecondary"
                    type="button"
                    onClick={() => {
                      setDraft((d) => (d ? ({ ...d, projectId: null } as any) : d));
                    }}
                    disabled={!draft.projectId}
                    title={t("invoiceDetail.clearProjectNote")}
                  >
                    {t("invoiceDetail.clearProjectBtn")}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Bill To */}
          <div className="card cardTight" style={{ marginTop: 14 }}>
            <div className="cardHeader">
              <h3 className="cardTitle">{t("invoiceDetail.billToTitle")}</h3>
              <div className="cardSub">{t("invoiceDetail.billToSub")}</div>
            </div>

            <div className="invBillToGrid">
              <div className="field">
                <label className="label">{t("invoiceDetail.billToName")}</label>
                <input
                  className="input"
                  value={(draft.billToName || "") === "TBD" ? "" : (draft.billToName || "")}
                  onChange={(e) => setDraftField("billToName", e.target.value)}
                  placeholder={t("invoiceDetail.billToNamePlaceholder")}
                />
              </div>

              <div className="field">
                <label className="label">{t("invoiceDetail.billToAddress")}</label>
                <input
                  className="input"
                  value={(draft.billToAddress || "") === "TBD" ? "" : (draft.billToAddress || "")}
                  onChange={(e) => setDraftField("billToAddress", e.target.value)}
                  placeholder={t("invoiceDetail.billToAddressPlaceholder")}
                />
              </div>
            </div>
          </div>

          {/* Scope */}
          <div className="card cardTight" style={{ marginTop: 14 }}>
            <div className="cardHeader">
              <h3 className="cardTitle">{t("invoiceDetail.scopeTitle")}</h3>
              <div className="cardSub">{t("invoiceDetail.scopeSub")}</div>
            </div>

            <div className="field">
              <label className="label">{t("invoiceDetail.scopeLabel")}</label>
              <textarea
                className="input"
                value={draft.scopeText}
                onChange={(e) => setDraftField("scopeText", e.target.value)}
                rows={5}
                placeholder={t("invoiceDetail.scopePlaceholder")}
                style={{ resize: "vertical" }}
              />
            </div>
          </div>

          {/* Line Items */}
          <div className="card" style={{ marginTop: 14 }}>
            <div className="cardHeader">
              <h3 className="cardTitle">{t("invoiceDetail.lineItemsTitle")}</h3>
              <div className="cardSub">{t("invoiceDetail.lineItemCount", { count: items.length })}</div>
            </div>

            <div className="tableWrap">
              <table>
                <thead>
                  <tr>
                    <th>{t("invoiceDetail.colDescription")}</th>
                    <th style={{ width: 140, textAlign: "right" }}>{t("invoiceDetail.colAmount")}</th>
                    <th style={{ width: 120 }}>{t("invoiceDetail.colTaxable")}</th>
                    <th style={{ width: 90, textAlign: "right" }}>{t("invoiceDetail.colRemove")}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => (
                    <tr key={idx}>
                      <td>
                        <input
                          className="input"
                          value={it.description}
                          onChange={(e) => setItem(idx, { description: e.target.value })}
                          placeholder={idx === 0 ? "Scope (or first line item)" : "Line item description"}
                          style={{ width: "100%" }}
                        />
                      </td>

                      <td style={{ textAlign: "right" }}>
                        <input
                          className="input"
                          value={Number(it.amount || 0).toString()}
                          onChange={(e) => setItem(idx, { amount: Number(e.target.value) || 0 })}
                          inputMode="decimal"
                          style={{ width: "100%", textAlign: "right" }}
                        />
                      </td>

                      <td>
                        <select
                          className="input"
                          value={it.taxable ? "yes" : "no"}
                          onChange={(e) => setItem(idx, { taxable: e.target.value === "yes" })}
                        >
                          <option value="no">{t("invoiceDetail.taxableNo")}</option>
                          <option value="yes">{t("invoiceDetail.taxableYes")}</option>
                        </select>
                      </td>

                      <td style={{ textAlign: "right" }}>
                        <button
                          className="btn btnSm btnSecondary"
                          type="button"
                          onClick={() => removeItem(idx)}
                          title={t("invoiceDetail.removeLineItem")}
                        >
                          −
                        </button>
                      </td>
                    </tr>
                  ))}

                  <tr>
                    <td colSpan={4}>
                      <div className="actionsRow" style={{ justifyContent: "flex-start" }}>
                        <button className="btn btnSm" type="button" onClick={addItem}>
                          {t("invoiceDetail.addLineItemBtn")}
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="pageNote" style={{ marginTop: 10 }}>
              {t("invoiceDetail.taxableNote")}
            </div>
          </div>

          {/* Tax + Totals */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
              gap: 14,
              marginTop: 14,
            }}
          >
            <div className="card cardTight">
              <div className="cardHeader">
                <h3 className="cardTitle">{t("invoiceDetail.taxTitle")}</h3>
                <div className="cardSub">{t("invoiceDetail.taxSub")}</div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 14 }}>
                <div className="field">
                  <label className="label">{t("invoiceDetail.taxEnabledLabel")}</label>
                  <select
                    className="input"
                    value={draft.taxEnabled ? "yes" : "no"}
                    onChange={(e) => setDraftField("taxEnabled", e.target.value === "yes")}
                  >
                    <option value="no">{t("invoiceDetail.taxableNo")}</option>
                    <option value="yes">{t("invoiceDetail.taxableYes")}</option>
                  </select>
                </div>

                <div className="field">
                  <label className="label">{t("invoiceDetail.taxRateLabel")}</label>

                  <input
                    className="input"
                    type="number"
                    step="0.001"
                    inputMode="decimal"
                    value={clamp((Number(draft.taxRate) || 0) * 100, 0, 100).toString()}
                    onChange={(e) => {
                      setTaxRateTouched(true);
                      const pct = e.target.value === "" ? 0 : Number.parseFloat(e.target.value);
                      setDraftField("taxRate", clamp(Number.isFinite(pct) ? pct : 0, 0, 100) / 100);
                    }}
                    disabled={!draft.taxEnabled}
                  />

                  <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 10 }}>
                    <button
                      className="btn btnSm btnSecondary"
                      type="button"
                      disabled={!draft.taxEnabled}
                      onClick={() => {
                        setTaxRateTouched(false);
                        setDraftField("taxRate", 0.06625);
                      }}
                      title={t("invoiceDetail.njTaxRateTip")}
                    >
                      {t("invoiceDetail.useNjRateBtn")}
                    </button>

                    {!taxRateTouched && Number(draft.taxRate) === 0.06625 ? (
                      <div className="pageNote" style={{ margin: 0 }}>
                        NJ 6.625%
                      </div>
                    ) : null}
                  </div>

                  <div className="pageNote" style={{ marginTop: 6 }}>
                    {t("invoiceDetail.taxRateNote")}
                  </div>
                </div>
              </div>
            </div>

            <div className="card cardTight">
              <div className="cardHeader">
                <h3 className="cardTitle">{t("invoiceDetail.totalsTitle")}</h3>
                <div className="cardSub">{draft.status === "final" ? t("invoiceDetail.finalizeBtn") : t("invoiceDetail.revertDraftBtn")}</div>
              </div>

              <div style={{ display: "grid", gap: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                  <div className="pageNote" style={{ margin: 0 }}>
                    {t("invoiceDetail.subtotalLabel")}
                  </div>
                  <div style={{ fontWeight: 800 }}>{money(totals.subtotal)}</div>
                </div>

                {totals.adjTotal !== 0 ? (
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                    <div className="pageNote" style={{ margin: 0 }}>
                      {t("invoiceDetail.adjustmentsLabel")}
                    </div>
                    <div style={{ fontWeight: 800 }}>{money(totals.adjTotal)}</div>
                  </div>
                ) : null}

                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                  <div className="pageNote" style={{ margin: 0 }}>
                    {t("invoiceDetail.taxLabel")}
                  </div>
                  <div style={{ fontWeight: 800 }}>{money(totals.tax)}</div>
                </div>

                <div
                  style={{
                    borderTop: "1px solid var(--border)",
                    paddingTop: 10,
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                  }}
                >
                  <div style={{ fontWeight: 900 }}>{t("invoiceDetail.totalLabel")}</div>
                  <div style={{ fontWeight: 900 }}>{money(totals.total)}</div>
                </div>

                {/* Optional: show applied vs remaining */}
                <div style={{ borderTop: "1px dashed var(--border)", paddingTop: 10, marginTop: 4 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                    <div className="pageNote" style={{ margin: 0 }}>
                      {t("invoiceDetail.paymentsAppliedLabel")}
                    </div>
                    <div style={{ fontWeight: 800 }}>{money(appliedTotal)}</div>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginTop: 6 }}>
                    <div className="pageNote" style={{ margin: 0 }}>
                      {t("invoiceDetail.balanceDueLabel")}
                    </div>
                    <div style={{ fontWeight: 900 }}>{money(Math.max(0, totals.total - appliedTotal))}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="card cardTight" style={{ marginTop: 14 }}>
            <div className="cardHeader">
              <h3 className="cardTitle">{t("invoiceDetail.notesTitle")}</h3>
              <div className="cardSub">{t("invoiceDetail.notesOptional")}</div>
            </div>

            <div className="field">
              <label className="label">{t("invoiceDetail.notesLabel")}</label>
              <textarea
                className="input"
                value={draft.notesText}
                onChange={(e) => setDraftField("notesText", e.target.value)}
                rows={4}
                placeholder={t("invoiceDetail.notesPlaceholder")}
                style={{ resize: "vertical" }}
              />
            </div>
          </div>

          {/* Payments Applied (READ-ONLY: apply existing payments only) */}
            <div className="card" style={{ marginTop: 14 }}>
            <div className="cardHeader">
                <h3 className="cardTitle">{t("invoiceDetail.paymentsAppliedTitle")}</h3>
                <div className="cardSub">
                {draft.projectId ? t("invoiceDetail.paymentsAppliedSub") : t("invoiceDetail.selectProjectForPayments")}
                </div>
            </div>

            {!draft.projectId ? (
                <div className="pageNote" style={{ marginTop: 8 }}>
                {t("invoiceDetail.oneOffPaymentsNote")}
                </div>
            ) : (
                <>
                {payErr ? (
                    <div className="formError" role="alert" style={{ marginTop: 10 }}>
                    {payErr}
                    </div>
                ) : null}

                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginTop: 10 }}>
                    <div className="pageNote" style={{ margin: 0 }}>
                    {payLoading ? t("invoiceDetail.loadingPayments") : t("invoiceDetail.paymentsFound", { count: payRows.length })}
                    </div>

                    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                    <div className="pageNote" style={{ margin: 0 }}>
                        {t("invoiceDetail.appliedTotalLabel")} <b>{money(appliedTotal)}</b>
                    </div>

                    <button
                        className="btn btnSm btnSecondary"
                        type="button"
                        onClick={() => loadPaymentsForProject(draft.projectId || null)}
                        disabled={payLoading}
                        title={t("invoiceDetail.refreshPaymentsBtn")}
                    >
                        {t("invoiceDetail.refreshPaymentsBtn")}
                    </button>

                    <button
                        className="btn btnSm btnSecondary"
                        type="button"
                        onClick={() => navigate(`/projects/${draft.projectId}?tab=payments`)}
                        title={t("invoiceDetail.openPaymentsTabBtn")}
                    >
                        {t("invoiceDetail.openPaymentsTabBtn")}
                    </button>
                    </div>
                </div>

                <div className="tableWrap" style={{ marginTop: 10 }}>
                    <table>
                    <thead>
                        <tr>
                        <th style={{ width: 90 }}>{t("invoiceDetail.colApplied")}</th>
                        <th style={{ width: 140 }}>{t("invoiceDetail.colDate")}</th>
                        <th style={{ width: 120 }}>{t("invoiceDetail.colMethod")}</th>
                        <th style={{ width: 140, textAlign: "right" }}>{t("invoiceDetail.colAmount")}</th>
                        <th>{t("invoiceDetail.colNotes")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {payRows.length === 0 ? (
                        <tr>
                            <td colSpan={5}>
                            <div className="pageNote" style={{ padding: "10px 0" }}>
                                {t("invoiceDetail.noPaymentsYet")}
                            </div>
                            </td>
                        </tr>
                        ) : (
                        payRows.map((p) => {
                            const isApplied = appliedIds.has(p.id);
                            return (
                            <tr key={p.id}>
                                <td>
                                <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <input
                                    type="checkbox"
                                    checked={isApplied}
                                    onChange={() => toggleAppliedPayment(p.id)}
                                    />
                                    <span className="pageNote" style={{ margin: 0 }}>
                                    {isApplied ? t("invoiceDetail.taxableYes") : t("invoiceDetail.taxableNo")}
                                    </span>
                                </label>
                                </td>
                                <td>{p.date}</td>
                                <td>{p.method}</td>
                                <td style={{ textAlign: "right", fontWeight: 800 }}>{money(p.amount)}</td>
                                <td style={{ whiteSpace: "pre-wrap" }}>{p.notes || ""}</td>
                            </tr>
                            );
                        })
                        )}
                    </tbody>
                    </table>
                </div>

                <div className="pageNote" style={{ marginTop: 10 }}>
                    {t("invoiceDetail.applyPaymentNote")}
                </div>
                </>
            )}
        </div>
        
        {/* Print Preview */}
          <div className="card" style={{ marginTop: 14 }}>
            <div className="cardHeader">
              <h3 className="cardTitle">{t("invoiceDetail.printPreviewTitle")}</h3>
              <div className="cardSub">{t("invoiceDetail.printPreviewSub")}</div>
          </div>

            {companyInfoErr ? (
            <div className="pageNote" style={{ marginTop: 8 }}>
                <span className="mutedError">Company info:</span> {companyInfoErr}
            </div>
            ) : null}

            <div id="invoice-print-preview">
            <InvoicePrintPreview
                company={companyInfo}
                invoiceNumber={draft.invoiceNumber}
                billToName={draft.billToName}
                billToAddress={draft.billToAddress}
                issueDate={draft.invoiceDate}
                dueDate={draft.dueDate || ""}
                items={items.map((it) => ({
                    description: it.description,
                    qty: 1,
                    rate: Number(it.amount) || 0,
                }))}
                  // ✅ NEW: show tax + payments in preview
                taxAmount={totals.tax}
                totalAmount={totals.total}
                amountPaid={appliedTotal}
                balanceDue={Math.max(0, totals.total - appliedTotal)}
                notes={draft.notesText || ""}
            />
            </div>
          </div>

          {/* (rest of your sections continue below, unchanged) */}
        </>
      )}
      </div>
      {/* PRINT-ONLY copy (hidden on screen, printed only) */}
        <div className="printOnly">
        <div className="printArea">
            <InvoicePrintPreview
            company={companyInfo}
            invoiceNumber={draft?.invoiceNumber}
            billToName={draft?.billToName}
            billToAddress={draft?.billToAddress}
            issueDate={draft?.invoiceDate}
            dueDate={draft?.dueDate || ""}
            items={(items || []).map((it) => ({
                description: it.description,
                qty: 1,
                rate: Number(it.amount) || 0,
            }))}
            taxAmount={totals.tax}
            totalAmount={totals.total}
            amountPaid={appliedTotal}
            balanceDue={Math.max(0, totals.total - appliedTotal)}
            notes={draft?.notesText || ""}
            />
        </div>
        </div>
    </div>
  );
}

